import React, { useRef, useState, useEffect } from "react";
import { Toast } from "primereact/toast";
import { FileUpload } from "primereact/fileupload";
import { ProgressBar } from "primereact/progressbar";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Dropdown } from "primereact/dropdown";
import { TabView, TabPanel } from "primereact/tabview";
import { Dialog } from "primereact/dialog";

import { fetchForms } from "./api";
import api from "../../Services/api";
import { saveAs } from "file-saver";
import usePageTitle from "../usePageTitle";
import "../ExcelForm/ExcelForm.css";

const Excels = () => {
  usePageTitle("Excels - SkeyeForm™");

  const [selectedForm, setSelectedForm] = useState(null);
  const [forms, setForms] = useState([]);
  const toast = useRef(null);
  const [totalSize, setTotalSize] = useState(0);
  const fileUploadRef = useRef(null);
  const [errorDialogVisible, setErrorDialogVisible] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    const getForms = async () => {
      try {
        const formsData = await fetchForms();
        setForms(formsData);
      } catch (error) {
        console.error("Error fetching forms:", error);
      }
    };

    getForms();
  }, []);

  const onTemplateSelect = (e) => {
    let _totalSize = totalSize;
    let files = e.files;

    Object.keys(files).forEach((key) => {
      _totalSize += files[key].size || 0;
    });

    setTotalSize(_totalSize);
  };

  const onTemplateRemove = (file, callback) => {
    setTotalSize(totalSize - file.size);
    callback();
  };

  const onTemplateClear = () => {
    setTotalSize(0);
  };

  const headerTemplate = (options) => {
    const { className, chooseButton, uploadButton, cancelButton } = options;
    const value = totalSize / 100000;
    const formatedValue =
      fileUploadRef && fileUploadRef.current
        ? fileUploadRef.current.formatSize(totalSize)
        : "0 B";

    return (
      <div
        className={className}
        style={{
          backgroundColor: "transparent",
          display: "flex",
          alignItems: "center",
        }}
      >
        {chooseButton}
        {uploadButton}
        {cancelButton}
        <div className="flex align-items-center gap-3 ml-auto">
          <span>{formatedValue} / 10 MB</span>
          <ProgressBar
            value={value}
            showValue={false}
            style={{ width: "10rem", height: "12px" }}
          ></ProgressBar>
        </div>
      </div>
    );
  };

  const itemTemplate = (file, props) => {
    return (
      <div className="flex align-items-center flex-wrap">
        <div
          className="flex align-items-center"
          style={{
            width: window.innerWidth > 768 ? "40%" : "100%",
            marginBottom: window.innerWidth > 768 ? 0 : "0.5rem",
          }}
        >
          <i
            className="pi pi-file-excel"
            style={{
              fontSize: "2rem",
              color: "#107C41", // Excel green color
              marginRight: "0.5rem",
              width: "48px",
              height: "48px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          ></i>
          <span
            className="flex flex-column text-left ml-3"
            style={{ width: "calc(100% - 60px)" }}
          >
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100%",
                display: "block",
              }}
            >
              {file.name}
            </span>
            <small>{new Date().toLocaleDateString()}</small>
          </span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: window.innerWidth > 768 ? "60%" : "100%",
          }}
        >
          <Tag
            value={props.formatSize}
            severity="warning"
            className="px-3 py-2"
          />
          <Button
            type="button"
            icon="pi pi-times"
            className="p-button-outlined p-button-rounded p-button-danger"
            onClick={() => onTemplateRemove(file, props.onRemove)}
          />
        </div>
      </div>
    );
  };

  const emptyTemplate = () => {
    return (
      <div className="flex align-items-center flex-column">
        <i
          className="pi pi-file-excel mt-3 p-5"
          style={{
            fontSize: window.innerWidth > 768 ? "5em" : "3em",
            borderRadius: "50%",
            backgroundColor: "var(--surface-b)",
            color: "#107C41", // Excel green color
          }}
        ></i>
        <span
          style={{
            fontSize: window.innerWidth > 768 ? "1.2em" : "1em",
            color: "var(--text-color-secondary)",
            textAlign: "center",
            padding: "0 1rem",
          }}
          className="my-5"
        >
          Drag and Drop Excel File Here
        </span>
      </div>
    );
  };

  const chooseOptions = {
    icon: "pi pi-fw pi-file-excel",
    iconOnly: true,
    className: "custom-choose-btn p-button-rounded p-button-outlined",
  };

  const cancelOptions = {
    icon: "pi pi-fw pi-times",
    iconOnly: true,
    className:
      "custom-cancel-btn p-button-danger p-button-rounded p-button-outlined",
  };

  const handleFormChange = (e) => {
    setSelectedForm(e.value);
  };

  const handleGenerateClick = async () => {
    if (!selectedForm) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please select a form",
        life: 3000,
      });
      return;
    }

    const data = {
      id: selectedForm.id,
      title: selectedForm.title,
      label: `${selectedForm.label}#${new Date().toISOString().split("T")[0]}`,
      description: selectedForm.description,
      createdAt: new Date().toISOString(),
      properties: selectedForm.properties,
      status: "up",
      password: null,
      timer: null,
      timerStart: null,
      timerEnd: null,
    };

    try {
      const response = await api.post("/excel/generate", data, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `${selectedForm.title}_template.xlsx`);
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Excel generated successfully",
        life: 3000,
      });
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Error generating Excel",
        life: 3000,
      });
      console.error("Error generating Excel:", error);
    }
  };

  const handleUpload = async ({ files }) => {
    if (!selectedForm || !files.length) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please select a form and upload a file.",
        life: 3000,
      });
      return;
    }
    const file = files[0];
    const validExcelTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (
      !validExcelTypes.includes(file.type) ||
      !["xlsx"].includes(fileExtension)
    ) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please upload only Excel files (.xlsx)",
        life: 3000,
      });
      return;
    }

    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    const userId = String(userData.id);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("formSchemaId", selectedForm.id);
    formData.append("submissionId", userId);

    try {
      // eslint-disable-next-line
      const response = await api.post("/excel/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "File uploaded successfully",
        life: 3000,
      });
    } catch (error) {
      // Handle array of errors from the API
      if (
        error.response?.data?.errors &&
        Array.isArray(error.response.data.errors)
      ) {
        // Store errors in state and show dialog instead of toast
        setValidationErrors(error.response.data.errors);
        setErrorDialogVisible(true);
      } else {
        // Fallback to generic error toast
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: error.response?.data?.message || "Error uploading file",
          life: 3000,
        });
      }
    }
  };

  const errorDialogFooter = (
    <div>
      <Button
        label="Close"
        icon="pi pi-times"
        onClick={() => setErrorDialogVisible(false)}
        className="p-button-text"
      />
    </div>
  );

  return (
    <div className="p-grid p-dir-col table-page" style={{ width: "100%" }}>
      <Toast ref={toast} />
      <Dialog
        header="Excel Validation Errors"
        visible={errorDialogVisible}
        style={{
          width: window.innerWidth > 768 ? "80vw" : "98vw",
          height: window.innerWidth > 768 ? "80vh" : "95vh",
          maxWidth: window.innerWidth > 768 ? "none" : "98vw",
        }}
        modal
        footer={errorDialogFooter}
        onHide={() => setErrorDialogVisible(false)}
      >
        {validationErrors.length > 0 && (
          <div>
            <p className="text-lg font-bold mb-3">
              Please correct the following errors in your Excel file:
            </p>
            <div
              className="border-1 surface-border border-round p-3"
              style={{ maxHeight: "500px", overflow: "auto" }}
            >
              <ul className="list-none p-0 m-0">
                {validationErrors.map((error, index) => (
                  <li
                    key={index}
                    className="p-2 border-bottom-1 surface-border flex align-items-center"
                  >
                    <i className="pi pi-exclamation-circle text-red-500 mr-2"></i>
                    <span>{error}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </Dialog>
      <div className="p-col title">
        <h1>Excel</h1>
        <div className="title-bar" style={{ marginBottom: "1em" }}></div>
      </div>
      <div className="table-container">
        <div className="p-col" style={{ width: "100%" }}>
          <TabView>
            <TabPanel
              header="Generate Excel"
              leftIcon="pi pi-file-excel mr-2 mb-2"
            >
              <label
                htmlFor="forms-list"
                style={{
                  fontSize: "1em",
                  fontWeight: "bold",
                  marginBottom: "0.5em",
                }}
              >
                Select a form to generate an Excel template:
              </label>
              <Dropdown
                value={selectedForm}
                filter
                options={forms}
                onChange={handleFormChange}
                placeholder="Select a Form"
                style={{
                  width: "100%",
                  marginBottom: "1em",
                  marginTop: "0.5em",
                }}
              />
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  label="Generate"
                  onClick={handleGenerateClick}
                  style={{ backgroundColor: "#0d2e4d", border: "none" }}
                />
              </div>
            </TabPanel>
            <TabPanel header="Upload Excel" leftIcon="pi pi-upload mr-2 mb-2">
              <label
                htmlFor="forms-list"
                style={{
                  fontSize: "1em",
                  fontWeight: "bold",
                  marginBottom: "0.5em",
                }}
              >
                Select a form to link the Excel file to:
              </label>
              <Dropdown
                value={selectedForm}
                filter
                options={forms}
                onChange={handleFormChange}
                placeholder="Select a Form"
                style={{
                  width: "100%",
                  marginBottom: "1em",
                  marginTop: "0.5em",
                }}
                required
              />
              <FileUpload
                ref={fileUploadRef}
                customUpload
                uploadHandler={handleUpload}
                name="excel[]"
                multiple
                accept=".xlsx,.xls"
                maxFileSize={1000000}
                onSelect={onTemplateSelect}
                onError={onTemplateClear}
                onClear={onTemplateClear}
                headerTemplate={headerTemplate}
                itemTemplate={itemTemplate}
                emptyTemplate={emptyTemplate}
                chooseOptions={chooseOptions}
                cancelOptions={cancelOptions}
                required
                uploadOptions={{
                  className: "p-button p-component p-disabled",
                  icon: "none",
                  style: {
                    backgroundColor: "#fff",
                    color: "#fff",
                    border: "none",
                    width: "0px",
                    height: "0px",
                  },
                }}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "1em",
                }}
              >
                <Button
                  label="Submit"
                  style={{ backgroundColor: "#0d2e4d", border: "none" }}
                  onClick={() => fileUploadRef.current.upload()}
                />
              </div>
            </TabPanel>
          </TabView>
        </div>
      </div>
    </div>
  );
};

export default Excels;
